/**
 * Browser-side session helpers: which project is active, and what happens when
 * the API says the login is no longer good.
 *
 * Nothing here stores or logs a credential value beyond the two token keys the
 * app already uses; clearing removes them.
 */
import { STORAGE_KEYS } from '../constants/storageKeys';

/** Fired once on `window` when the API rejects the signed-in token (401). */
export const SESSION_ENDED_EVENT = 'ans:session-ended';

function read(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

/**
 * The active project (uuid). A choice saved under the pre-port `wedding` key is
 * migrated to the new key the first time it is read, so nobody has to pick
 * again after the switch.
 */
export function getActiveProjectId(): string | null {
  const current = read(STORAGE_KEYS.PROJECT_ID);
  if (current) return current;
  const legacy = read(STORAGE_KEYS.LEGACY_WEDDING_ID);
  if (legacy) {
    try {
      localStorage.setItem(STORAGE_KEYS.PROJECT_ID, legacy);
      localStorage.removeItem(STORAGE_KEYS.LEGACY_WEDDING_ID);
    } catch { /* storage unavailable: still return the value */ }
    return legacy;
  }
  return null;
}

export function setActiveProjectId(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEYS.PROJECT_ID, id);
    localStorage.removeItem(STORAGE_KEYS.LEGACY_WEDDING_ID);
  } catch { /* storage unavailable */ }
}

/** Removes every stored credential. The chosen project is not a secret and stays. */
export function clearStoredSession(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.GG_AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.DEMO_SESSION);
    // The e-mail is sent as a viewer-level fallback header; leaving it behind
    // would keep identifying a signed-out browser.
    localStorage.removeItem(STORAGE_KEYS.PROFILE_OWNER_EMAIL);
  } catch { /* storage unavailable */ }
}

/**
 * Called by every API client when a response arrives. A 401 on a request that
 * carried the CURRENT token means the login has expired: clear it and tell the
 * app, once, so it can show a sign-in prompt instead of a page of failed
 * screens. Returns true when it ended the session.
 *
 * Ignored on purpose: other statuses (403 is "not allowed", not "signed out");
 * requests that sent no token (nothing to expire, and the dev bypass has none);
 * and late answers for a token that has since been replaced by a new sign-in.
 * Because the token is cleared first, the other requests still in flight when
 * a screen fans out fall into that last case, so only one event is sent.
 */
export function endSessionIfExpired(status: number, sentToken: string | null): boolean {
  if (status !== 401 || !sentToken) return false;
  if (read(STORAGE_KEYS.GG_AUTH_TOKEN) !== sentToken) return false;
  clearStoredSession();
  window.dispatchEvent(new Event(SESSION_ENDED_EVENT));
  return true;
}

/** Reloads the page. Wrapped so tests can observe it (jsdom cannot stub location). */
export function reloadApp(): void {
  window.location.reload();
}
